import React, { useState, useEffect, useMemo } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  Menu, 
  MenuItem, 
  styled, 
  Chip,
  Divider,
  useTheme,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Business from '@mui/icons-material/Business';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import firebaseDataService from '../../services/FirebaseDataService';
import { getAssignmentsByUser } from '../../services/migration/branchPersonnelAssignmentDataService';
import { useNavigate } from 'react-router-dom';
import NotificationMenu from '../notifications/NotificationMenu';
import ManagerWithSidebar from './ManagerWithSidebar';
import ChangePasswordModal from '../layout/ChangePasswordModal';
import { BRAND } from '../../config/brand';

// Sidebar'ı gizlemek için CSS stil eklentisi
const GlobalStyle = styled('style')({
  '@global': {
    // Bu stil manager sayfasında olduğumuzda ana sidebar'ı gizler
    '.gts-web-src-components-layout-Sidebar-module__SidebarContainer, .MuiDrawer-root > .MuiPaper-root': {
      display: 'none !important',
    }
  }
});

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: 'white',
  color: theme.palette.text.primary,
  boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
  position: 'fixed',
  width: '100%',
  zIndex: 99,
}));

const UserChip = styled(Chip)(({ theme }) => ({
  height: 42,
  borderRadius: 21,
  padding: theme.spacing(0.5, 1),
  backgroundColor: '#0D47A1',
  transition: 'all 0.2s ease-in-out',
  '& .MuiChip-label': {
    color: 'white',
    transition: 'color 0.2s ease-in-out',
  },
  '&:hover': {
    backgroundColor: 'white',
    '& .MuiChip-label': {
      color: '#0D47A1',
    },
    '& .MuiIconButton-root': {
      color: '#0D47A1',
    },
  },
}));

const BranchChip = styled(Chip)<{ clickable?: boolean }>(({ theme, clickable }) => ({
  marginLeft: theme.spacing(2),
  backgroundColor: '#1976D2',
  color: 'white',
  cursor: clickable ? 'pointer' : 'default',
  '& .MuiSvgIcon-root': {
    color: 'white',
  },
  '&:hover': clickable ? {
    backgroundColor: theme.palette.primary.dark,
  } : {},
}));

interface AssignedBranch {
  id: string;
  name: string;
}

const ManagerDashboard: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [branchMenuAnchorEl, setBranchMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [branchName, setBranchName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>(BRAND.fullName);
  const { currentUser, logout, userDetails } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [assignedBranches, setAssignedBranches] = useState<AssignedBranch[]>([]);
  const [activeBranchId, setActiveBranchId] = useState<string | null>(null);
  const [loadingBranches, setLoadingBranches] = useState<boolean>(true);

  // Sifre guncelleme modal + basari snackbar state'leri
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordChangedSnackOpen, setPasswordChangedSnackOpen] = useState(false);

  // own-auth: Firebase `providerData` artik yok (her zaman undefined). Oturum acmis
  // her own-auth kullanicisi email/parola degisimi yapabilir → giris varsa true.
  const isPasswordProvider = useMemo(() => {
    return !!currentUser;
  }, [currentUser]);
  
  useEffect(() => {
    const fetchBranchData = async () => {
      if (userDetails && userDetails.companyId && currentUser) {
        setLoadingBranches(true);
        try {
          // Sirket adi SQL kimlik verisinden (userDetails) geliyor; RTDB sirket dugumu okumasi kaldirildi (Faz E).
          setCompanyName(userDetails.companyName || BRAND.fullName);

          let tempAssignedBranches: AssignedBranch[] = [];
          const primaryBranchId = userDetails.branchesId;

          // Subeleri TEK SEFER cek; hem birincil sube hem ek atamalar bu liste uzerinden cozulur.
          const allBranches = await firebaseDataService.getBranches(userDetails.companyId || '');

          if (primaryBranchId) {
            const primaryBranch = allBranches.find(b => b.id === primaryBranchId);
            if (primaryBranch) {
              tempAssignedBranches.push({ id: primaryBranchId, name: primaryBranch.name || 'Bilinmeyen Şube' });
            }
          }

          // Ek sube atamalari artik SQL'den okunur (eski RTDB branch_personnel_assignments dugumu kaldirildi).
          const assignments = await getAssignmentsByUser(currentUser.uid);
          assignments.forEach(assignment => {
            if (assignment.branchId && assignment.branchId !== primaryBranchId) {
              const branch = allBranches.find(b => b.id === assignment.branchId);
              tempAssignedBranches.push({ id: assignment.branchId, name: branch?.name || 'Bilinmeyen Şube' });
            }
          });

          const uniqueBranchesMap = new Map<string, AssignedBranch>();
          tempAssignedBranches.forEach(b => uniqueBranchesMap.set(b.id, b));
          const finalBranches = Array.from(uniqueBranchesMap.values());
          setAssignedBranches(finalBranches);

          if (finalBranches.length > 0) {
            const currentActive = activeBranchId && finalBranches.some(b => b.id === activeBranchId) 
              ? activeBranchId 
              : (primaryBranchId && finalBranches.some(b => b.id === primaryBranchId) ? primaryBranchId : finalBranches[0].id);
            setActiveBranchId(currentActive);
          } else {
            setActiveBranchId(null);
          }

        } catch (error) {
          console.error("Şube ve şirket verileri yüklenirken hata:", error);
          setAssignedBranches([]);
          setActiveBranchId(null);
        } finally {
          setLoadingBranches(false);
        }
      }
    };
    fetchBranchData();
  }, [userDetails, currentUser]);

  useEffect(() => {
    if (activeBranchId && assignedBranches.length > 0) {
      const currentBranch = assignedBranches.find(b => b.id === activeBranchId);
      setBranchName(currentBranch ? currentBranch.name : 'Şube Bulunamadı');
    } else if (assignedBranches.length === 0 && !loadingBranches) {
      setBranchName('');
    } else if (!activeBranchId && !loadingBranches) {
      setBranchName('');
    }
  }, [activeBranchId, assignedBranches, loadingBranches]);
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBranchMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (assignedBranches.length > 1) {
      setBranchMenuAnchorEl(event.currentTarget);
    }
  };

  const handleBranchMenuClose = () => {
    setBranchMenuAnchorEl(null);
  };

  const handleBranchSelect = (branchId: string) => {
    setActiveBranchId(branchId);
    handleBranchMenuClose();
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
    } catch (error) {
      console.error('Çıkış yapılırken hata oluştu:', error);
    }
  };
  
  const userFullName = userDetails 
    ? `${userDetails.firstName} ${userDetails.lastName}` 
    : 'Kullanıcı';

  return (
    <>
      {/* Global CSS stil eklentisi - ana sidebar'ı gizler */}
      <GlobalStyle />
      
      <StyledAppBar elevation={0}>
        <Toolbar>
          <Typography 
            variant="h5"
            component="h1" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 'bold', 
              color: '#0D47A1',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {companyName}
            
            {loadingBranches ? (
                <CircularProgress size={20} sx={{ ml: 2, color: '#1976D2' }} />
            ) : branchName ? (
              <BranchChip
                icon={<Business />}
                label={branchName}
                size="medium"
                clickable={assignedBranches.length > 1}
                onClick={handleBranchMenuOpen}
                deleteIcon={assignedBranches.length > 1 ? <ExpandMoreIcon /> : undefined}
                onDelete={assignedBranches.length > 1 ? handleBranchMenuOpen : undefined}
              />
            ) : null}
            
            <Menu
              anchorEl={branchMenuAnchorEl}
              open={Boolean(branchMenuAnchorEl)}
              onClose={handleBranchMenuClose}
              PaperProps={{
                sx: { width: 250, mt: 1 }
              }}
            >
              {assignedBranches.map((branch) => (
                <MenuItem 
                  key={branch.id} 
                  selected={branch.id === activeBranchId}
                  onClick={() => handleBranchSelect(branch.id)}
                >
                  {branch.name}
                </MenuItem>
              ))}
            </Menu>
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NotificationMenu />
            
            <UserChip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {userFullName}
                  </Typography>
                  <Box 
                    component="span"
                    onClick={handleMenuOpen}
                    sx={{ 
                      ml: 0.5, 
                      p: 0, 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center' 
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </Box>
                </Box>
              }
              onClick={handleMenuOpen}
              sx={{ cursor: 'pointer' }}
            />
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: { width: 200, mt: 1 }
              }}
            >
              <MenuItem onClick={() => {
                handleMenuClose();
                navigate('/manager/profile');
              }}>
                Profil
              </MenuItem>
              {/* Sifre guncelleme — sadece email/password kullanicilari icin aktif.
                  Google kullanicilari icin disabled + Tooltip ile bilgilendirme. */}
              <Tooltip
                title={!isPasswordProvider ? (t('header.changePasswordDisabledTooltip') as string) : ''}
                placement="left"
                arrow
                disableHoverListener={isPasswordProvider}
                disableFocusListener={isPasswordProvider}
              >
                <span>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      setChangePasswordOpen(true);
                    }}
                    disabled={!isPasswordProvider}
                    sx={{ width: '100%' }}
                  >
                    <LockResetIcon fontSize="small" sx={{ mr: 1.5, color: 'action.active' }} />
                    {t('header.changePassword')}
                  </MenuItem>
                </span>
              </Tooltip>
              <Divider />
              <MenuItem onClick={handleLogout}>
                Güvenli Çıkış
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </StyledAppBar>
      
      <Box 
        component="main" 
        sx={{ 
          position: 'fixed',
          top: 64,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ManagerWithSidebar activeBranchId={activeBranchId} />
      </Box>

      {/* Sifre Guncelleme Modal — Firebase Auth reauthenticate + updatePassword */}
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onSuccess={() => setPasswordChangedSnackOpen(true)}
      />

      {/* Sifre guncelleme basari bildirimi */}
      <Snackbar
        open={passwordChangedSnackOpen}
        autoHideDuration={4000}
        onClose={() => setPasswordChangedSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setPasswordChangedSnackOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('changePasswordModal.success')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ManagerDashboard; 